import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useIntakeFormStore,
  caregiverAddressSchema,
  TCaregiverAddressSchema,
  SUPPORTED_PROVINCE_CODES,
} from "../intakeFormState";
// UI
import { IntakeFormScreenContainer } from "../IntakeFormScreenContainer";
import { TextInput } from "../../../components/TextInput/TextInput";
import { SelectInput } from "../../../components/SelectInput/SelectInput";
import { NextButton } from "../NextButton";
// controller (from layout)
import { useOutletContext } from "react-router-dom";
import type { IntakeOutletContext } from "../IntakeFormLayout";
// utils
import { trackEvent } from "../../../utils/daniel-analytics";

export const IntakeFormAddressScreen = React.memo(() => {
  const { caregiverAddress, handleCaregiverAddressSubmit } = useIntakeFormStore();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<TCaregiverAddressSchema>({
    resolver: yupResolver(caregiverAddressSchema),
    reValidateMode: "onBlur",
    mode: "onBlur",
    defaultValues: caregiverAddress,
  });

  const outletContext: IntakeOutletContext = useOutletContext();
  const { onSubmit: afterSubmit } = outletContext;

  const onSubmit = useCallback(
    (data: TCaregiverAddressSchema) => {
      console.log("data", data);
      // TODO: NEED TO CHECK CONSENT STATUS AND FLASH A TOAST IF THEY DON'T CONSENT
      handleCaregiverAddressSubmit(data);
      afterSubmit();
    },
    [handleCaregiverAddressSubmit, afterSubmit],
  );

  const provinceCode = watch("provinceCode");
  useEffect(() => {
    trackEvent("provinceCode-selected", { provinceCode });
  }, [provinceCode]);

  return (
    <IntakeFormScreenContainer title={t("intakeAddress.header")}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          {...register("address1")}
          autoComplete="address-line1"
          isRequired
          label={t("intakeAddress.address1")}
          hookFormFieldError={errors.address1}
        />

        <div className="mt-1">
          <TextInput
            {...register("address2")}
            autoComplete="address-line2"
            isRequired={false}
            label={t("intakeAddress.address2")}
            hookFormFieldError={errors.address2}
          />
        </div>

        <div className="mt-1">
          <TextInput
            {...register("city")}
            autoComplete="address-level2"
            isRequired
            label={t("intakeAddress.city")}
            hookFormFieldError={errors.city}
          />
        </div>

        <div className="mt-1 flex justify-between">
          <div className="flex-[1]">
            <SelectInput
              label={t("intakeAddress.provinceCode")}
              autoComplete="address-level1"
              choices={SUPPORTED_PROVINCE_CODES.map((provinceCode) => {
                return { value: provinceCode, label: t(`intakeAddress.provinceCodeChoices.${provinceCode}`) };
              })}
              isRequired
              {...register("provinceCode")}
              hookFormFieldError={errors.provinceCode}
            />
          </div>
          <div className="ml-4 flex-[1]">
            <TextInput
              {...register("zipCode")}
              autoComplete="postal-code"
              isRequired
              label={t("intakeAddress.zipCode")}
              hookFormFieldError={errors.zipCode}
            />
          </div>
          <div className="flex-[2]" />
        </div>

        <NextButton />
      </form>
    </IntakeFormScreenContainer>
  );
});

IntakeFormAddressScreen.displayName = "IntakeFormAddressScreen";
